import * as angular from "angular";
import template from "./view-modes-section.html";
import "./view-modes-section.less";
import { ViewModeActionHandle } from "./viewModeActionHandle";
import { ViewModeHandle } from "./viewModeHandle";
import { ViewModesCollection } from "./viewModesCollection";
import type { ViewModeFallbackController } from "./view-mode-fallback";
import type {
	ViewModeIntent,
	ViewModeTransition,
	ViewModesOnModeChange
} from "./view-modes-section-types";

const buttonClasses: Record<ViewModeIntent, string> = {
	default: "btn-default",
	primary: "btn-primary",
	accent: "btn-success",
	success: "btn-success",
	warning: "btn-warning",
	danger: "btn-danger",
	link: "btn-link"
};

export const viewModesSectionTransclude = {
	title: "viewModesSectionTitle",
	fallback: "?viewModeFallback",
	modes: "viewModes"
};

export interface ViewModesSectionController {
	readonly initialMode?: string;
	isLoading?: boolean;
	bodyClass?: string;
	onModeChange?: ViewModesOnModeChange;
}

// AngularJS owns these merged binding properties; keeping them type-only preserves pre-assigned values.
// eslint-disable-next-line no-redeclare
export class ViewModesSectionController {
	public static $inject = ["$scope"];

	public readonly modes: ViewModesCollection;
	public activeMode?: ViewModeHandle;
	public pendingAction?: ViewModeActionHandle;
	public fallback?: ViewModeFallbackController;
	public isTransitioning = false;

	public constructor(private readonly $scope: angular.IScope) {
		this.modes = new ViewModesCollection(() => this.syncActiveMode());
	}

	public get activeActions(): ViewModeActionHandle[] {
		return this.activeMode?.actions.filter(action => this.hasSelectableTarget(action)) ?? [];
	}

	public registerFallback(fallback: ViewModeFallbackController): void {
		if(this.fallback && this.fallback !== fallback) {
			throw new Error("view-modes-section supports one view-mode-fallback.");
		}

		this.fallback = fallback;
		this.requestDigest();
	}

	public isFallbackActive(): boolean {
		return this.fallback?.active === true;
	}

	public isBusy(): boolean {
		return this.isLoading === true
			|| this.activeMode?.isLoading === true
			|| (this.isFallbackActive() && this.fallback?.loading === true)
			|| !!this.pendingAction
			|| this.isTransitioning;
	}

	public isDirty(): boolean {
		return this.activeMode?.isDirty() === true;
	}

	public canInvokeAction(action: ViewModeActionHandle): boolean {
		return action.mode === this.activeMode
			&& action.isVisible
			&& !action.isDisabled
			&& this.hasSelectableTarget(action)
			&& !this.isFallbackActive()
			&& !this.isBusy();
	}

	public async invokeAction(action: ViewModeActionHandle): Promise<boolean> {
		if(!this.canInvokeAction(action)) {
			return false;
		}

		this.pendingAction = action;
		this.requestDigest();

		try {
			const result = await action.invoke();
			if(result === false) {
				return false;
			}

			if(!action.targetMode) {
				return true;
			}

			const target = this.modes.findSelectable(action.targetMode);
			if(!target) {
				return false;
			}

			return this.transitionTo(target, action);
		}
		finally {
			this.pendingAction = undefined;
			this.requestDigest();
		}
	}

	public getButtonClass(item: { intent: ViewModeIntent }): string {
		return buttonClasses[item.intent];
	}

	private async transitionTo(
		target: ViewModeHandle,
		action: ViewModeActionHandle
	): Promise<boolean> {
		if(target === this.activeMode) {
			return true;
		}
		if(!target.isSelectable || this.isTransitioning) {
			return false;
		}

		const current = this.activeMode;
		const transition: ViewModeTransition = {
			from: current,
			to: target,
			reason: "action",
			action
		};

		this.isTransitioning = true;
		this.requestDigest();

		try {
			const leaveResult = await current?.leave(transition);
			if(leaveResult === false) {
				return false;
			}
			if(current?.isDirty()) {
				return false;
			}

			const enterResult = await target.enter(transition);
			if(enterResult === false) {
				return false;
			}

			this.commitActiveMode(target, true);
			return true;
		}
		finally {
			this.isTransitioning = false;
			this.requestDigest();
		}
	}

	private hasSelectableTarget(action: ViewModeActionHandle): boolean {
		return !action.targetMode || !!this.modes.findSelectable(action.targetMode);
	}

	private syncActiveMode(): void {
		const nextMode = this.modes.resolveMode([
			this.initialMode,
			this.activeMode?.code,
			this.modes.firstSelectable?.code
		]);
		if(nextMode) {
			this.commitActiveMode(nextMode, false);
		}
	}

	private commitActiveMode(mode: ViewModeHandle, notify: boolean): void {
		if(mode === this.activeMode) {
			return;
		}

		const previousMode = this.activeMode;
		this.activeMode = mode;
		this.requestDigest();

		if(notify) {
			this.onModeChange?.({
				mode: mode.code,
				previousMode: previousMode?.code,
				$mode: mode,
				$previousMode: previousMode
			});
		}
	}

	private requestDigest(): void {
		this.$scope.$evalAsync(() => {});
	}
}

export default angular
	.module("vesl.components.view-modes-section.component", [])
	.component("viewModesSection", {
		template,
		transclude: viewModesSectionTransclude,
		controller: ViewModesSectionController,
		bindings: {
			initialMode: "<?",
			isLoading: "<?",
			bodyClass: "@?",
			onModeChange: "&?"
		}
	})
	.name;
